import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import { Description, Download, Draw } from '@mui/icons-material';
import { apiService } from '../../services/api';
import { useProviderAgreementGate } from './ProviderAgreementSigningDialog';

interface CustomerAgreementRow {
  id: string;
  status: 'pending' | 'signed';
  serviceLabel: string;
  providerName: string;
  customer: { street?: string; city?: string; zipCode?: string };
  requestedAt?: string | null;
  signedAt?: string | null;
  fileName?: string | null;
}

/** Customer dashboard: agreements waiting for signature and signed copies to download. */
const CustomerAgreementsCard: React.FC = () => {
  const [rows, setRows] = useState<CustomerAgreementRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);
  const { requestSignatures, agreementDialog } = useProviderAgreementGate();

  const load = useCallback(async () => {
    try {
      const res = await apiService.get<CustomerAgreementRow[]>('/customer-agreements/mine');
      if (res.success && Array.isArray(res.data)) setRows(res.data);
    } catch (err: any) {
      console.warn('[AGREEMENTS] Failed to load customer agreements:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleSign = async (row: CustomerAgreementRow) => {
    setError('');
    setSuccess('');
    const signed = await requestSignatures({ agreementId: row.id });
    if (signed) setSuccess('Agreement signed. A copy was emailed to you.');
    await load();
  };

  const handleDownload = async (row: CustomerAgreementRow) => {
    setBusyId(row.id);
    setError('');
    try {
      const res = await apiService.get<{ url: string }>(`/customer-agreements/${row.id}/download-url`);
      if (!res.success || !res.data?.url) throw new Error(res.error || 'Download failed');
      window.location.href = res.data.url;
    } catch (err: any) {
      setError(err?.message || 'Download failed');
    } finally {
      setBusyId(null);
    }
  };

  if (loading || rows.length === 0) return <>{agreementDialog}</>;

  const pending = rows.filter((r) => r.status === 'pending');
  const signed = rows.filter((r) => r.status === 'signed');

  return (
    <Card sx={{ mb: 4 }}>
      <CardContent>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Description color="primary" />
          Service Agreements
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

        {pending.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            You have {pending.length} agreement{pending.length === 1 ? '' : 's'} waiting for your signature.
          </Alert>
        )}

        <Stack spacing={1.5}>
          {[...pending, ...signed].map((row) => (
            <Box
              key={row.id}
              sx={{
                display: 'flex',
                alignItems: { xs: 'flex-start', sm: 'center' },
                justifyContent: 'space-between',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 1,
                p: 1.5,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
              }}
            >
              <Box>
                <Typography fontWeight={600}>
                  {row.providerName} — {row.serviceLabel}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {[row.customer?.street, row.customer?.city].filter(Boolean).join(', ')}
                  {row.status === 'signed' && row.signedAt
                    ? ` · Signed ${new Date(row.signedAt).toLocaleDateString()}`
                    : ''}
                </Typography>
              </Box>
              {row.status === 'pending' ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip size="small" color="warning" label="Needs signature" />
                  <Button variant="contained" size="small" startIcon={<Draw />} onClick={() => handleSign(row)}>
                    Review &amp; Sign
                  </Button>
                </Box>
              ) : (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={busyId === row.id ? <CircularProgress size={16} /> : <Download />}
                  onClick={() => handleDownload(row)}
                  disabled={busyId === row.id}
                >
                  Download PDF
                </Button>
              )}
            </Box>
          ))}
        </Stack>
      </CardContent>
      {agreementDialog}
    </Card>
  );
};

export default CustomerAgreementsCard;
